"use client";

import { c as _c } from "react/compiler-runtime";
import { useGameLocale, useT } from "@/lib/i18n";
import { hreflangOf } from "@/lib/locale";
/**
 * In-game-style summary of a run, mimicking the victory/defeat screen.
 * Renders three act rows of map node icons + relic strip + card grid.
 */

import { useEffect, useState, type ReactNode } from "react";
import type { TFn } from "@/lib/i18n";
import { Link } from "@/i18n/navigation";
import TinyCard from "@/app/components/TinyCard";
import { CardPill, RelicPill, PotionPill, cleanId, displayName, type CardInfo, type RelicInfo, type PotionInfo } from "./RunPills";
export type { PotionInfo };
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
import { imageUrl } from "@/lib/image-url";
import { stackCards } from "@/lib/deck-stack";
import { fmtDateTime, fmtDateTimePacific } from "@/lib/pacific";
const ICON_BASE = imageUrl("/static/images/ui/run_history");
interface DeckCard {
  id: string;
  current_upgrade_level?: number;
  enchantment?: {
    id: string;
    amount: number;
    floor_added_to_deck?: number;
  } | null;
}
interface RunRelic {
  id: string;
  floor_added_to_deck?: number;
}
interface PlayerStats {
  current_hp?: number;
  max_hp?: number;
  current_gold?: number;
  damage_taken?: number;
  hp_healed?: number;
  gold_gained?: number;
  gold_spent?: number;
  card_choices?: Array<{
    card: {
      id: string;
    };
    was_picked: boolean;
  }>;
  cards_gained?: Array<{
    id: string;
  }>;
  cards_removed?: Array<{
    id: string;
  }>;
  upgraded_cards?: string[];
  rest_site_choices?: string[];
  potion_used?: string[];
  relic_choices?: Array<{
    choice: string;
    was_picked: boolean;
  }>;
  event_choices?: Array<{
    title?: {
      key?: string;
    };
  }>;
}
interface Room {
  model_id?: string;
  room_type?: string;
  monster_ids?: string[];
  turns_taken?: number;
}
interface MapPoint {
  map_point_type: string;
  rooms?: Room[];
  player_stats?: PlayerStats[];
}
interface Player {
  character: string;
  deck: DeckCard[];
  relics: RunRelic[];
  potions?: {
    id: string;
    slot_index: number;
  }[];
  max_potion_slot_count?: number;
}
interface Run {
  win: boolean;
  was_abandoned: boolean;
  ascension?: number;
  run_time?: number;
  seed?: string;
  build_id?: string;
  game_mode?: string;
  acts?: string[];
  start_time?: number;
  killed_by_encounter?: string;
  modifiers?: string[];
  map_point_history?: MapPoint[][];
  players: Player[];
  /** Attached server-side from the runs DB row (not in the on-disk
   *  run JSON). Missing for anonymous submissions. */
  username?: string;
}
const RARITY_ORDER = ["Starter", "Common", "Uncommon", "Rare", "Ancient", "Event", "Token", "Status", "Curse", "Quest"];

// Matches NDeckHistoryEntry.Reload() in the game:
//   enchanted → StsColors.purple
//   upgraded  → StsColors.green
//   else      → default label color
function cardLabelColor(upgraded: boolean, enchanted: boolean): string {
  if (enchanted) return "text-[var(--color-necrobinder)]";
  if (upgraded) return "text-[var(--color-silent)]";
  return "text-[var(--text-primary)]";
}
const MAP_POINT_LABELS: Record<string, string> = {
  monster: "Monster",
  elite: "Elite",
  boss: "Boss",
  event: "Event",
  treasure: "Treasure",
  rest_site: "Rest Site",
  shop: "Shop",
  ancient: "Ancient",
  unknown: "Unknown"
};
const TIER_LABELS: Record<string, string> = {
  weak: "Weak",
  normal: "Normal",
  elite: "Elite",
  boss: "Boss"
};
const TIER_OUTLINE: Record<string, string> = {
  weak: "ring-1 ring-success/40",
  normal: "ring-1 ring-warning/40",
  elite: "ring-1 ring-warning/60",
  boss: "ring-2 ring-danger/60"
};
function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
const DATE_STYLE = {
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit"
} as const;

// Server-rendered pages must emit the same string everywhere, so the first
// paint is Pacific; after hydration it becomes the viewer's local time.
function modeLabel(mode: string | undefined): string {
  if (!mode) return "Standard";
  return mode.charAt(0).toUpperCase() + mode.slice(1).toLowerCase();
}
function formatDate(epoch: number | undefined, local: boolean, locale: string): string {
  if (!epoch) return "";
  return local ? fmtDateTime(epoch * 1000, DATE_STYLE, locale) : fmtDateTimePacific(epoch * 1000, DATE_STYLE, locale);
}

/** Decide the tier ("weak"|"normal"|"elite"|"boss") for an encounter. */
function encounterTier(modelId: string | undefined, mapPointType: string): "weak" | "normal" | "elite" | "boss" | "" {
  if (!modelId) return "";
  if (modelId.endsWith("_BOSS")) return "boss";
  if (modelId.endsWith("_ELITE") || mapPointType === "elite") return "elite";
  if (modelId.endsWith("_WEAK")) return "weak";
  if (modelId.endsWith("_NORMAL")) return "normal";
  return mapPointType === "monster" ? "normal" : "";
}

/** Derive a spire-codex page href from an entity id by its prefix. */
function entityHref(id: string, bp: string): string | null {
  if (!id || id === "NONE.NONE") return null;
  const slug = cleanId(id).toLowerCase();
  if (id.startsWith("MONSTER.")) return `${bp}/monsters/${slug}`;
  if (id.startsWith("ENCOUNTER.")) return `${bp}/encounters/${slug}`;
  if (id.startsWith("EVENT.")) return `${bp}/events/${slug}`;
  if (id.startsWith("RELIC.")) return `${bp}/relics/${slug}`;
  if (id.startsWith("CARD.")) return `${bp}/cards/${slug}`;
  if (id.startsWith("POTION.")) return `${bp}/potions/${slug}`;
  if (id.startsWith("CHARACTER.")) return `${bp}/characters/${slug}`;
  return null;
}

/** Resolve the icon filename for a map point. */
function iconFor(mp: MapPoint, buildId?: string): {
  src: string;
  betaSrc?: string;
  tier: string;
  label: string;
} {
  const room = mp.rooms?.[0];
  const modelId = room?.model_id || "";
  const tier = encounterTier(modelId, mp.map_point_type);

  // Main run_history path plus a beta-versioned fallback. Beta-only content
  // (e.g. the AEONGLASS boss) only has its map icon under the beta tree, so a
  // beta build's run must fall back to /beta/<build_id>/... when the main path
  // 404s. Known main icons never 404, so the fallback only fires when needed.
  const resolve = (slug: string) => ({
    src: `${ICON_BASE}/${slug}.webp`,
    betaSrc: buildId ? imageUrl(`/static/images/beta/${buildId}/ui/run_history/${slug}.webp`) : undefined
  });
  if (mp.map_point_type === "boss" && modelId.endsWith("_BOSS")) {
    const slug = cleanId(modelId).toLowerCase();
    return {
      ...resolve(slug),
      tier,
      label: displayName(modelId)
    };
  }
  if (mp.map_point_type === "ancient" && modelId.startsWith("EVENT.")) {
    const slug = cleanId(modelId).toLowerCase();
    return {
      ...resolve(slug),
      tier: "",
      label: displayName(modelId)
    };
  }
  const typeMap: Record<string, string> = {
    monster: "monster",
    elite: "elite",
    event: "event",
    treasure: "treasure",
    rest_site: "rest_site",
    shop: "shop",
    unknown: "event"
  };
  const slug = typeMap[mp.map_point_type] ?? "monster";
  const label = modelId ? displayName(modelId) : displayName(mp.map_point_type);
  return {
    ...resolve(slug),
    tier,
    label
  };
}
interface Props {
  run: Run;
  player: Player;
  cardData: Record<string, CardInfo>;
  relicData: Record<string, RelicInfo>;
  potionData: Record<string, PotionInfo>;
  charColor: string;
  langPrefix: string;
  charName: string;
  encounterName?: string;
  actNames: Record<string, string>;
  monsterNames: Record<string, string>;
}
export default function RunSummary(t0) {
  const $ = _c(185);
  const {
    run,
    player,
    cardData,
    relicData,
    potionData,
    charColor,
    langPrefix: bp,
    charName,
    encounterName,
    actNames,
    monsterNames
  } = t0;
  const t = useT();
  const dateLocale = hreflangOf(useGameLocale());
  let t1;
  if ($[0] !== cardData) {
    t1 = id => cardData[cleanId(id)]?.name ?? displayName(id);
    $[0] = cardData;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const cardName = t1;
  let t2;
  if ($[2] !== relicData) {
    t2 = id_0 => relicData[cleanId(id_0)]?.name ?? displayName(id_0);
    $[2] = relicData;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const relicName = t2;
  const [mounted, setMounted] = useState(false);
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setMounted(true);
    t4 = [];
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== run) {
    t5 = lastPlayerStats(run);
    $[6] = run;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const finalStats = t5;
  let t6;
  if ($[8] !== run.map_point_history) {
    t6 = run.map_point_history ?? [];
    $[8] = run.map_point_history;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = t6.reduce(_temp, 0);
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const totalFloors = t7;
  let t8;
  if ($[12] !== player.character) {
    t8 = cleanId(player.character).toLowerCase();
    $[12] = player.character;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const charSlug = t8;
  const t9 = `/static/images/characters/character_icon_${charSlug}.webp`;
  let t10;
  if ($[14] !== t9) {
    t10 = imageUrl(t9);
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const charIcon = t10;
  const potionSlots = player.max_potion_slot_count ?? 3;
  let t11;
  if ($[16] !== player.potions) {
    t11 = player.potions ?? [];
    $[16] = player.potions;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const playerPotions = t11;
  let t12;
  if ($[18] !== charName || $[19] !== encounterName || $[20] !== run.was_abandoned || $[21] !== run.win || $[22] !== t) {
    t12 = run.win ? t("{char} ascended.", {
      char: charName
    }) : run.was_abandoned ? t("The journey ended.") : encounterName ? t("{char} fell to {encounter}.", {
      char: charName,
      encounter: encounterName
    }) : t("{char} fell.", {
      char: charName
    });
    $[18] = charName;
    $[19] = encounterName;
    $[20] = run.was_abandoned;
    $[21] = run.win;
    $[22] = t;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const deathQuote = t12;
  let t13;
  if ($[24] !== relicData) {
    t13 = r => relicData[cleanId(r.id)]?.rarity;
    $[24] = relicData;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== player.relics || $[27] !== t13) {
    t14 = bucketByRarity(player.relics, t13);
    $[26] = player.relics;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const relicsByRarity = t14;
  let t15;
  if ($[29] !== cardData) {
    t15 = c => cardData[cleanId(c.id)]?.rarity;
    $[29] = cardData;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== player.deck || $[32] !== t15) {
    t16 = bucketByRarity(player.deck, t15);
    $[31] = player.deck;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const cardsByRarity = t16;
  const stackedCards = stackCards(player.deck, cardData);
  const t17 = "rounded-xl border p-4 sm:p-5 mb-4";
  const t18 = `color-mix(in srgb, ${charColor} 35%, transparent)`;
  const t19 = `color-mix(in srgb, ${charColor} 6%, var(--bg-card))`;
  let t20;
  if ($[34] !== t18 || $[35] !== t19) {
    t20 = {
      borderColor: t18,
      background: t19
    };
    $[34] = t18;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  const t21 = `${bp}/characters/${charSlug}`;
  let t22;
  if ($[37] !== charColor) {
    t22 = {
      borderColor: charColor
    };
    $[37] = charColor;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== charIcon || $[40] !== charName || $[41] !== t22) {
    t23 = <img src={charIcon} alt={charName} className="w-9 h-9 rounded-full object-cover border-2" style={t22} crossOrigin="anonymous" />;
    $[39] = charIcon;
    $[40] = charName;
    $[41] = t22;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== t21 || $[44] !== t23) {
    t24 = <Link href={t21} className="flex-shrink-0">{t23}</Link>;
    $[43] = t21;
    $[44] = t23;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = imageUrl("/static/images/ui/top_bar/top_bar_heart.webp");
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t) {
    t26 = t("HP");
    $[47] = t;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  const t27 = `${finalStats?.current_hp ?? "?"}/${finalStats?.max_hp ?? "?"}`;
  let t28;
  if ($[49] !== t26 || $[50] !== t27) {
    t28 = <IconStat icon={t25} alt={t26} value={t27} color="var(--color-ironclad)" />;
    $[49] = t26;
    $[50] = t27;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = imageUrl("/static/images/ui/top_bar/top_bar_gold.webp");
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  let t30;
  if ($[53] !== t) {
    t30 = t("Gold");
    $[53] = t;
    $[54] = t30;
  } else {
    t30 = $[54];
  }
  const t31 = finalStats?.current_gold ?? "?";
  let t32;
  if ($[55] !== t30 || $[56] !== t31) {
    t32 = <IconStat icon={t29} alt={t30} value={t31} color="var(--accent-gold)" />;
    $[55] = t30;
    $[56] = t31;
    $[57] = t32;
  } else {
    t32 = $[57];
  }
  let t33;
  if ($[58] !== bp || $[59] !== playerPotions || $[60] !== potionData || $[61] !== potionSlots) {
    t33 = <PotionSlots potions={playerPotions} total={potionSlots} potionData={potionData} bp={bp} />;
    $[58] = bp;
    $[59] = playerPotions;
    $[60] = potionData;
    $[61] = potionSlots;
    $[62] = t33;
  } else {
    t33 = $[62];
  }
  let t34;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = imageUrl("/static/images/ui/top_bar/top_bar_map.webp");
    $[63] = t34;
  } else {
    t34 = $[63];
  }
  let t35;
  if ($[64] !== t) {
    t35 = t("Floor");
    $[64] = t;
    $[65] = t35;
  } else {
    t35 = $[65];
  }
  let t36;
  if ($[66] !== t35 || $[67] !== totalFloors) {
    t36 = <IconStat icon={t34} alt={t35} value={totalFloors} />;
    $[66] = t35;
    $[67] = totalFloors;
    $[68] = t36;
  } else {
    t36 = $[68];
  }
  let t37;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = imageUrl("/static/images/ui/top_bar/timer_icon.webp");
    $[69] = t37;
  } else {
    t37 = $[69];
  }
  let t38;
  if ($[70] !== t) {
    t38 = t("Time");
    $[70] = t;
    $[71] = t38;
  } else {
    t38 = $[71];
  }
  const t39 = run.run_time ?? 0;
  let t40;
  if ($[72] !== t39) {
    t40 = formatTime(t39);
    $[72] = t39;
    $[73] = t40;
  } else {
    t40 = $[73];
  }
  let t41;
  if ($[74] !== t38 || $[75] !== t40) {
    t41 = <IconStat icon={t37} alt={t38} value={t40} />;
    $[74] = t38;
    $[75] = t40;
    $[76] = t41;
  } else {
    t41 = $[76];
  }
  let t42;
  if ($[77] !== run.ascension || $[78] !== t) {
    t42 = (run.ascension ?? 0) > 0 && <IconStat icon={imageUrl("/static/images/ui/top_bar/top_bar_ascension.webp")} alt={t("Ascension")} value={`A${run.ascension}`} color="var(--accent-gold)" />;
    $[77] = run.ascension;
    $[78] = t;
    $[79] = t42;
  } else {
    t42 = $[79];
  }
  let t43;
  if ($[80] !== bp || $[81] !== run.username || $[82] !== t) {
    t43 = run.username && <div className="truncate"><Link href={`${bp}/runs?username=${encodeURIComponent(run.username)}`} className="text-[var(--text-secondary)] hover:text-[var(--accent-gold)] hover:underline" title={t("View all runs by this player")}>{t("by")}{" "}<span className="font-medium text-[var(--text-primary)]">{run.username}</span></Link></div>;
    $[80] = bp;
    $[81] = run.username;
    $[82] = t;
    $[83] = t43;
  } else {
    t43 = $[83];
  }
  const t44 = run.start_time && <div className="truncate" suppressHydrationWarning={true}>{formatDate(run.start_time, mounted, dateLocale)}</div>;
  let t45;
  if ($[84] !== run.seed || $[85] !== t) {
    t45 = run.seed && <div className="truncate">{t("Seed")}: <span className="font-mono">{run.seed}</span></div>;
    $[84] = run.seed;
    $[85] = t;
    $[86] = t45;
  } else {
    t45 = $[86];
  }
  let t46;
  if ($[87] !== run.game_mode || $[88] !== t) {
    t46 = t(modeLabel(run.game_mode));
    $[87] = run.game_mode;
    $[88] = t;
    $[89] = t46;
  } else {
    t46 = $[89];
  }
  let t47;
  if ($[90] !== run.build_id) {
    t47 = run.build_id && <span className="ml-1">· {run.build_id}</span>;
    $[90] = run.build_id;
    $[91] = t47;
  } else {
    t47 = $[91];
  }
  let t48;
  if ($[92] !== t46 || $[93] !== t47) {
    t48 = <div className="truncate">{t46}{t47}</div>;
    $[92] = t46;
    $[93] = t47;
    $[94] = t48;
  } else {
    t48 = $[94];
  }
  let t49;
  if ($[95] !== t43 || $[96] !== t44 || $[97] !== t45 || $[98] !== t48) {
    t49 = <div className="w-full sm:w-auto sm:ml-auto text-left sm:text-right text-xs text-[var(--text-muted)] leading-tight">{t43}{t44}{t45}{t48}</div>;
    $[95] = t43;
    $[96] = t44;
    $[97] = t45;
    $[98] = t48;
    $[99] = t49;
  } else {
    t49 = $[99];
  }
  let t50;
  if ($[100] !== t24 || $[101] !== t28 || $[102] !== t32 || $[103] !== t33 || $[104] !== t36 || $[105] !== t41 || $[106] !== t42 || $[107] !== t49) {
    t50 = <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm mb-3 pb-3 border-b border-[var(--border-subtle)]">{t24}{t28}{t32}{t33}{t36}{t41}{t42}{t49}</div>;
    $[100] = t24;
    $[101] = t28;
    $[102] = t32;
    $[103] = t33;
    $[104] = t36;
    $[105] = t41;
    $[106] = t42;
    $[107] = t49;
    $[108] = t50;
  } else {
    t50 = $[108];
  }
  let t51;
  if ($[109] !== deathQuote) {
    t51 = <div className="mb-4 italic text-sm text-[var(--text-secondary)]">“{deathQuote}”</div>;
    $[109] = deathQuote;
    $[110] = t51;
  } else {
    t51 = $[110];
  }
  let t52;
  if ($[111] !== run.map_point_history) {
    t52 = run.map_point_history ?? [];
    $[111] = run.map_point_history;
    $[112] = t52;
  } else {
    t52 = $[112];
  }
  let t53;
  if ($[113] !== actNames || $[114] !== bp || $[115] !== cardName || $[116] !== monsterNames || $[117] !== relicName || $[118] !== run.acts || $[119] !== run.build_id || $[120] !== run.map_point_history || $[121] !== t || $[122] !== t52) {
    let t54;
    if ($[124] !== actNames || $[125] !== bp || $[126] !== cardName || $[127] !== monsterNames || $[128] !== relicName || $[129] !== run.acts || $[130] !== run.build_id || $[131] !== run.map_point_history || $[132] !== t) {
      t54 = (act_0, i) => {
        const actName = run.acts?.[i] ? actNames[cleanId(run.acts[i]).toUpperCase()] ?? displayName(run.acts[i]) : t("Act {n}", {
          n: i + 1
        });
        const actStartFloor = (run.map_point_history ?? []).slice(0, i).reduce(_temp2, 0) + 1;
        return <div key={i} className="flex items-center gap-2 sm:gap-3"><div className="w-20 sm:w-24 text-xs font-medium text-[var(--text-secondary)] flex-shrink-0">{actName}</div><div className="flex flex-wrap items-center gap-1 flex-1">{act_0.map((mp, j) => <MapNode key={j} mp={mp} floorNum={actStartFloor + j} bp={bp} buildId={run.build_id} cardName={cardName} relicName={relicName} monsterNames={monsterNames} />)}</div></div>;
      };
      $[124] = actNames;
      $[125] = bp;
      $[126] = cardName;
      $[127] = monsterNames;
      $[128] = relicName;
      $[129] = run.acts;
      $[130] = run.build_id;
      $[131] = run.map_point_history;
      $[132] = t;
      $[133] = t54;
    } else {
      t54 = $[133];
    }
    t53 = t52.map(t54);
    $[113] = actNames;
    $[114] = bp;
    $[115] = cardName;
    $[116] = monsterNames;
    $[117] = relicName;
    $[118] = run.acts;
    $[119] = run.build_id;
    $[120] = run.map_point_history;
    $[121] = t;
    $[122] = t52;
    $[123] = t53;
  } else {
    t53 = $[123];
  }
  let t54;
  if ($[134] !== t53) {
    t54 = <div className="space-y-2 mb-5">{t53}</div>;
    $[134] = t53;
    $[135] = t54;
  } else {
    t54 = $[135];
  }
  let t55;
  if ($[136] !== t) {
    t55 = t("Relics");
    $[136] = t;
    $[137] = t55;
  } else {
    t55 = $[137];
  }
  let t56;
  if ($[138] !== player.relics.length || $[139] !== t55) {
    t56 = <span className="font-semibold">{t55} ({player.relics.length}):</span>;
    $[138] = player.relics.length;
    $[139] = t55;
    $[140] = t56;
  } else {
    t56 = $[140];
  }
  let t57;
  if ($[141] !== relicsByRarity || $[142] !== t) {
    t57 = <RaritySummary buckets={relicsByRarity} t={t} />;
    $[141] = relicsByRarity;
    $[142] = t;
    $[143] = t57;
  } else {
    t57 = $[143];
  }
  let t58;
  if ($[144] !== t56 || $[145] !== t57) {
    t58 = <div className="text-xs text-[var(--text-secondary)] mb-2">{t56}{" "}{t57}</div>;
    $[144] = t56;
    $[145] = t57;
    $[146] = t58;
  } else {
    t58 = $[146];
  }
  let t59;
  if ($[147] !== bp || $[148] !== player.relics || $[149] !== relicData) {
    let t60;
    if ($[151] !== bp || $[152] !== relicData) {
      t60 = (relic, i_0) => {
        const rid = cleanId(relic.id);
        const info = relicData[rid];
        return <RelicPill key={`${rid}-${i_0}`} relicId={rid} relicData={relicData} bp={bp} className="w-8 h-8 sm:w-9 sm:h-9 rounded-md bg-scrim/30 flex items-center justify-center hover:bg-scrim/50 transition-colors">{info?.image_url ? <img src={imageUrl(info.image_url)} alt={info.name} className="w-full h-full object-contain p-0.5" crossOrigin="anonymous" /> : <span className="text-[8px] text-[var(--text-muted)]">{rid.slice(0, 3)}</span>}</RelicPill>;
      };
      $[151] = bp;
      $[152] = relicData;
      $[153] = t60;
    } else {
      t60 = $[153];
    }
    t59 = player.relics.map(t60);
    $[147] = bp;
    $[148] = player.relics;
    $[149] = relicData;
    $[150] = t59;
  } else {
    t59 = $[150];
  }
  let t60;
  if ($[154] !== t59) {
    t60 = <div className="flex flex-wrap gap-1">{t59}</div>;
    $[154] = t59;
    $[155] = t60;
  } else {
    t60 = $[155];
  }
  let t61;
  if ($[156] !== t58 || $[157] !== t60) {
    t61 = <div className="mb-4">{t58}{t60}</div>;
    $[156] = t58;
    $[157] = t60;
    $[158] = t61;
  } else {
    t61 = $[158];
  }
  let t62;
  if ($[159] !== t) {
    t62 = t("Cards");
    $[159] = t;
    $[160] = t62;
  } else {
    t62 = $[160];
  }
  let t63;
  if ($[161] !== player.deck.length || $[162] !== t62) {
    t63 = <span className="font-semibold">{t62} ({player.deck.length}):</span>;
    $[161] = player.deck.length;
    $[162] = t62;
    $[163] = t63;
  } else {
    t63 = $[163];
  }
  let t64;
  if ($[164] !== cardsByRarity || $[165] !== t) {
    t64 = <RaritySummary buckets={cardsByRarity} t={t} />;
    $[164] = cardsByRarity;
    $[165] = t;
    $[166] = t64;
  } else {
    t64 = $[166];
  }
  let t65;
  if ($[167] !== t63 || $[168] !== t64) {
    t65 = <div className="text-xs text-[var(--text-secondary)] mb-2">{t63}{" "}{t64}</div>;
    $[167] = t63;
    $[168] = t64;
    $[169] = t65;
  } else {
    t65 = $[169];
  }
  const t66 = "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-1";
  let t67;
  if ($[170] !== bp || $[171] !== cardData) {
    t67 = (entry, i_1) => {
      const info_0 = cardData[entry.id];
      const colorClass = cardLabelColor(entry.upgraded, !!entry.enchantment);
      return <CardPill key={`${entry.id}-${entry.upgraded ? "u" : "n"}-${entry.enchantment ?? ""}-${i_1}`} cardId={entry.id} upgraded={entry.upgraded} enchantment={entry.enchantment} cardData={cardData} bp={bp} className="flex items-center gap-1.5 text-xs hover:bg-[var(--bg-card-hover)] rounded px-1 py-0.5 transition-colors"><TinyCard color={info_0?.color} type={info_0?.type} rarity={info_0?.rarity} /><span className={`truncate ${colorClass}`}>{entry.count > 1 && <span className="text-[var(--text-muted)] mr-1">{entry.count}x</span>}{info_0?.name || displayName(`CARD.${entry.id}`)}{entry.upgraded && "+"}</span></CardPill>;
    };
    $[170] = bp;
    $[171] = cardData;
    $[172] = t67;
  } else {
    t67 = $[172];
  }
  const t68 = stackedCards.map(t67);
  let t69;
  if ($[173] !== t68) {
    t69 = <div className={t66}>{t68}</div>;
    $[173] = t68;
    $[174] = t69;
  } else {
    t69 = $[174];
  }
  let t70;
  if ($[175] !== t65 || $[176] !== t69) {
    t70 = <div>{t65}{t69}</div>;
    $[175] = t65;
    $[176] = t69;
    $[177] = t70;
  } else {
    t70 = $[177];
  }
  let t71;
  if ($[178] !== t20 || $[179] !== t50 || $[180] !== t51 || $[181] !== t54 || $[182] !== t61 || $[183] !== t70) {
    t71 = <div className={t17} style={t20}>{t50}{t51}{t54}{t61}{t70}</div>;
    $[178] = t20;
    $[179] = t50;
    $[180] = t51;
    $[181] = t54;
    $[182] = t61;
    $[183] = t70;
    $[184] = t71;
  } else {
    t71 = $[184];
  }
  return t71;
}
function _temp2(sum_0, a) {
  return sum_0 + a.length;
}
function _temp(sum, act) {
  return sum + act.length;
}
function MapNode(t0) {
  const $ = _c(37);
  const {
    mp,
    floorNum,
    bp,
    buildId,
    cardName,
    relicName,
    monsterNames
  } = t0;
  const t = useT();
  const [show, setShow] = useState(false);
  let t1;
  if ($[0] !== buildId || $[1] !== mp) {
    t1 = iconFor(mp, buildId);
    $[0] = buildId;
    $[1] = mp;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    src,
    betaSrc,
    tier,
    label
  } = t1;
  const room = mp.rooms?.[0];
  const ps = mp.player_stats?.[0];
  const t2 = room?.model_id ?? "";
  let t3;
  if ($[3] !== bp || $[4] !== t2) {
    t3 = entityHref(t2, bp);
    $[3] = bp;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const href = t3;
  let t4;
  if ($[6] !== betaSrc) {
    t4 = e => {
      const img = e.currentTarget as HTMLImageElement;
      if (betaSrc && img.dataset.triedBeta !== "1") {
        img.dataset.triedBeta = "1";
        img.src = betaSrc;
        return;
      }
      img.style.display = "none";
    };
    $[6] = betaSrc;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== label || $[9] !== src || $[10] !== t4) {
    t5 = <img src={src} alt={label} className="w-full h-full object-contain p-0.5" crossOrigin="anonymous" onError={t4} />;
    $[8] = label;
    $[9] = src;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const iconImg = t5;
  let t6;
  if ($[12] !== cardName || $[13] !== floorNum || $[14] !== label || $[15] !== monsterNames || $[16] !== mp.map_point_type || $[17] !== ps || $[18] !== relicName || $[19] !== room || $[20] !== show || $[21] !== t || $[22] !== tier) {
    t6 = show && <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] shadow-xl pointer-events-none text-left"><div className="flex items-center justify-between mb-1.5"><div className="text-xs font-semibold text-[var(--text-primary)]">{label}</div><div className="text-[10px] text-[var(--text-muted)]">{t("Floor {n}", {
            n: floorNum
          })}</div></div><div className="text-[10px] text-[var(--text-muted)] mb-1.5 capitalize">{MAP_POINT_LABELS[mp.map_point_type] ? t(MAP_POINT_LABELS[mp.map_point_type]) : mp.map_point_type.replace(/_/g, " ")}{tier && ` · ${t(TIER_LABELS[tier])}`}{room?.turns_taken != null && ` · ${room.turns_taken} ${t("turns")}`}</div>{room?.monster_ids && room.monster_ids.length > 0 && <div className="text-[10px] text-[var(--text-secondary)] mb-1.5">{t("vs")}{" "}{room.monster_ids.map(m => monsterNames[cleanId(m).toUpperCase()] ?? displayName(m)).join(", ")}</div>}{ps && <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] text-[var(--text-muted)] mb-1.5"><span>{t("HP")} {ps.current_hp}/{ps.max_hp}</span>{(ps.damage_taken ?? 0) > 0 && <span style={{
          color: "var(--color-ironclad)"
        }}>-{ps.damage_taken}</span>}{(ps.hp_healed ?? 0) > 0 && <span style={{
          color: "var(--color-silent)"
        }}>+{ps.hp_healed} {t("HP")}</span>}{(ps.gold_gained ?? 0) > 0 && <span style={{
          color: "var(--accent-gold)"
        }}>+{ps.gold_gained}g</span>}{(ps.gold_spent ?? 0) > 0 && <span style={{
          color: "var(--text-muted)"
        }}>-{ps.gold_spent}g</span>}</div>}{ps?.cards_gained && ps.cards_gained.length > 0 && <div className="text-[10px] text-[var(--color-silent)] mb-0.5">+ {ps.cards_gained.map(c => cardName(c.id)).join(", ")}</div>}{ps?.cards_removed && ps.cards_removed.length > 0 && <div className="text-[10px] text-[var(--color-ironclad)] mb-0.5">− {ps.cards_removed.map(c_0 => cardName(c_0.id)).join(", ")}</div>}{ps?.upgraded_cards && ps.upgraded_cards.length > 0 && <div className="text-[10px] text-[var(--accent-gold)] mb-0.5">⬆ {ps.upgraded_cards.map(c_1 => cardName(c_1)).join(", ")}</div>}{ps?.relic_choices?.some(_temp3) && <div className="text-[10px] text-[var(--accent-gold)] mb-0.5">+{" "}{ps.relic_choices.filter(_temp4).map(r_0 => relicName(r_0.choice)).join(", ")}</div>}{(() => {
        const choiceKey = ps?.event_choices?.[0]?.title?.key;
        const choice = choiceKey ? humanizeChoiceKey(choiceKey) : "";
        return choice ? <div className="text-[10px] text-[var(--text-secondary)] mt-1 italic">{t("chose {choice}", {
            choice
          })}</div> : null;
      })()}<div className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 bg-[var(--bg-card)] border-r border-b border-[var(--border-subtle)] rotate-45 -mt-1" /></div>;
    $[12] = cardName;
    $[13] = floorNum;
    $[14] = label;
    $[15] = monsterNames;
    $[16] = mp.map_point_type;
    $[17] = ps;
    $[18] = relicName;
    $[19] = room;
    $[20] = show;
    $[21] = t;
    $[22] = tier;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const tooltip = t6;
  const wrapClass = `relative w-7 h-7 sm:w-8 sm:h-8 rounded-md bg-scrim/30 flex items-center justify-center ${TIER_OUTLINE[tier] ?? ""}`;
  if (href) {
    const t7 = `${wrapClass} hover:bg-scrim/50 hover:ring-2 hover:ring-[var(--accent-gold)]/60 transition-all`;
    let t8;
    let t9;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = () => setShow(true);
      t9 = () => setShow(false);
      $[24] = t8;
      $[25] = t9;
    } else {
      t8 = $[24];
      t9 = $[25];
    }
    let t10;
    if ($[26] !== href || $[27] !== iconImg || $[28] !== t7 || $[29] !== tooltip) {
      t10 = <Link href={href} className={t7} onMouseEnter={t8} onMouseLeave={t9}>{iconImg}{tooltip}</Link>;
      $[26] = href;
      $[27] = iconImg;
      $[28] = t7;
      $[29] = tooltip;
      $[30] = t10;
    } else {
      t10 = $[30];
    }
    return t10;
  }
  let t7;
  let t8;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setShow(true);
    t8 = () => setShow(false);
    $[31] = t7;
    $[32] = t8;
  } else {
    t7 = $[31];
    t8 = $[32];
  }
  let t9;
  if ($[33] !== iconImg || $[34] !== tooltip || $[35] !== wrapClass) {
    t9 = <span className={wrapClass} onMouseEnter={t7} onMouseLeave={t8}>{iconImg}{tooltip}</span>;
    $[33] = iconImg;
    $[34] = tooltip;
    $[35] = wrapClass;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  return t9;
}
function _temp4(r) {
  return r.was_picked;
}
function _temp3(r_1) {
  return r_1.was_picked;
}
function humanizeChoiceKey(key: string): string {
  // Event choices are stored as the game's localization key, e.g.
  // "MORPHIC_GROVE.pages.INITIAL.options.LONER.title" → "Loner". Pull the
  // segment after "options" when the event had a branching choice.
  const parts = key.split(".");
  const idx = parts.findIndex(p => p === "options");
  if (idx >= 0 && parts[idx + 1]) {
    return parts[idx + 1].replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  }
  // No "options" segment means there was no real choice: ancients and other
  // single-outcome events record a page key like
  // "ancients.NONUPEIPE.pages.DONE.description". Returning the raw dotted key
  // would leak it onto the page, so drop it (the caller skips the line). A
  // plain, dot-free label is already human and passes through.
  return key.includes(".") ? "" : key;
}
function IconStat(t0) {
  const $ = _c(11);
  const {
    icon,
    alt,
    value,
    color
  } = t0;
  let t1;
  if ($[0] !== alt || $[1] !== icon) {
    t1 = <img src={icon} alt={alt} className="w-5 h-5 object-contain" crossOrigin="anonymous" />;
    $[0] = alt;
    $[1] = icon;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== color) {
    t2 = color ? {
      color
    } : undefined;
    $[3] = color;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2 || $[6] !== value) {
    t3 = <span className="font-semibold" style={t2}>{value}</span>;
    $[5] = t2;
    $[6] = value;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t3) {
    t4 = <div className="flex items-center gap-1.5 text-xs sm:text-sm">{t1}{t3}</div>;
    $[8] = t1;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
function PotionSlots(t0) {
  const $ = _c(12);
  const {
    potions,
    total,
    potionData,
    bp
  } = t0;
  let t1;
  let t2;
  if ($[0] !== bp || $[1] !== potionData || $[2] !== potions || $[3] !== total) {
    const bySlot = Array(total).fill(null);
    for (const p of potions) {
      if (p.slot_index >= 0 && p.slot_index < total) {
        bySlot[p.slot_index] = p;
      }
    }
    t1 = "flex items-center gap-1";
    let t3;
    if ($[6] !== bp || $[7] !== potionData) {
      t3 = (p_0, i) => {
        if (!p_0) {
          return <span key={i} className="w-5 h-5 rounded-sm border border-dashed border-[var(--border-subtle)]" />;
        }
        const id = cleanId(p_0.id);
        const info = potionData[id];
        return <PotionPill key={i} potionId={id} potionData={potionData} bp={bp} className="w-5 h-5 flex items-center justify-center hover:scale-110 transition-transform">{info?.image_url ? <img src={imageUrl(info.image_url)} alt={info.name} className="w-5 h-5 object-contain" crossOrigin="anonymous" /> : <span className="w-5 h-5 rounded-sm bg-[var(--color-silent)]/50" />}</PotionPill>;
      };
      $[6] = bp;
      $[7] = potionData;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t2 = bySlot.map(t3);
    $[0] = bp;
    $[1] = potionData;
    $[2] = potions;
    $[3] = total;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  let t3;
  if ($[9] !== t1 || $[10] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
function RaritySummary(t0) {
  const $ = _c(5);
  const {
    buckets,
    t
  } = t0;
  let parts;
  if ($[0] !== buckets || $[1] !== t) {
    parts = [];
    for (const r of RARITY_ORDER) {
      const n = buckets.get(r);
      if (n) {
        parts.push(`${n} ${t(r)}`);
      }
    }
    $[0] = buckets;
    $[1] = t;
    $[2] = parts;
  } else {
    parts = $[2];
  }
  const t1 = parts.join(", ");
  let t2;
  if ($[3] !== t1) {
    t2 = <span className="text-[var(--text-muted)]">{t1}</span>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function bucketByRarity<T>(items: T[], getRarity: (item: T) => string | undefined): Map<string, number> {
  const m = new Map<string, number>();
  for (const item of items) {
    const r = getRarity(item) ?? "Unknown";
    m.set(r, (m.get(r) ?? 0) + 1);
  }
  return m;
}
function lastPlayerStats(run: Run): PlayerStats | undefined {
  const acts = run.map_point_history ?? [];
  for (let a = acts.length - 1; a >= 0; a--) {
    for (let f = acts[a].length - 1; f >= 0; f--) {
      const ps = acts[a][f]?.player_stats?.[0];
      if (ps) return ps;
    }
  }
  return undefined;
}